import { Dataset } from '@/types/datasets.ts';
import { DataInput, StepType } from '@/types/automationFields.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { Execution } from '@/types/runs.ts';
import { PaginatedResponse } from '@/types/common.ts';

export enum LogSeverity {
  Debug = 'debug',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}

export type StepExecutionLogEventKind = 'dataflow_input' | 'dataflow_output';

export type StepExecutionLogEntry = {
  id: string;
  is_internal: boolean;
  created_at: string;
  message: string;
  detail_message: string;
  severity: LogSeverity;
  event_kind?: StepExecutionLogEventKind | null;
  dataset: Dataset | null;
  extract_entity_prefix: string | null;
  extract_entity_id: string | null;
};
export type StepExecutionLogs = StepExecutionLogEntry[];
export type StepExecutionOutputDataset = {
  dataset: Dataset;
  output_id: string;
  binding_kind?: 'produced' | 'source_input' | 'alias' | null;
};
export type StepExecutionOutputDatasets = PaginatedResponse<StepExecutionOutputDataset>;
export type StepExecutionInputDataset = {
  dataset: Dataset;
  data_input: DataInput | null;
};
export type StepExecutionInputDatasets = PaginatedResponse<StepExecutionInputDataset>;
export type StepRunReference = {
  id: string;
  step_id: string;
  step_execution_id: string;
  step_type: StepType;
  step_index: number;
  full_path: string;
  parent_path: string | null;
  child_index: number | null;
  attempt_number: number;
};
export type StepRunConnection = {
  dataset: Dataset;
  data_input: DataInput | null;
  output_id: string | null;
  producer_step_run: StepRunReference | null;
  producer_output_id: string | null;
  provider_step_run?: StepRunReference | null;
  provider_output_id?: string | null;
  provider_binding_kind?: 'produced' | 'source_input' | 'alias' | null;
  origin_step_run?: StepRunReference | null;
  origin_output_id?: string | null;
  origin_binding_kind?: 'produced' | 'source_input' | 'alias' | null;
  binding_kind?: 'produced' | 'source_input' | 'alias' | null;
  consumer_step_run: StepRunReference | null;
};
export type StepRunConnections = {
  consumed: StepRunConnection[];
  produced: StepRunConnection[];
  consumed_by: StepRunConnection[];
  input_connections?: StepRunConnection[];
  output_bindings?: StepRunConnection[];
  downstream_consumers?: StepRunConnection[];
  used_existing_step_execution: boolean;
};
export type StepRunDataflowInput = {
  dataset: Dataset;
  data_input: DataInput | null;
};
export type StepRunDataflowOutput = {
  dataset: Dataset;
  output_id: string;
  binding_kind?: 'produced' | 'source_input' | 'alias' | null;
};
export type StepRunLoadedInput = StepRunDataflowInput & {
  binding_count: number;
  configured_roles: { group_key: string; input_index: number; label: string }[];
};
export type StepRunConditionalRoute = {
  condition_id: string;
  name: string;
  evaluation: 'matched' | 'not_matched' | 'not_evaluated' | 'unknown' | 'disabled';
  selected: boolean;
  justification: string | null;
  decision_dataset: Dataset | null;
};
export type StepRunLogPresentation = {
  inputs: StepRunLoadedInput[];
  activity: StepExecutionLogs;
  outputs: StepRunDataflowOutput[];
  conditional_routes: StepRunConditionalRoute[];
};
export type ScopedRetryGuidance = {
  attempt: number;
  child_scope_step_path: string;
  failed_step_path: string;
  failed_step_name: string;
  failed_step_type: StepType;
  target_step_paths: string[];
  targeting_strategy?: 'dependency_closure' | 'scope_prefix_fallback' | null;
};
export type ScopedRetryHistoryEntry = {
  attempt: number;
  failed_step_path: string;
  failed_step_name: string;
  failed_step_type: StepType;
  target_step_paths: string[];
  targeting_strategy?: 'dependency_closure' | 'scope_prefix_fallback' | null;
};
export type AttemptRetrySignal = {
  classification_status: string;
  reason_code: string;
  reason_message: string;
  should_retry: boolean;
  safe_to_retry: boolean;
  retry_scope?: string | null;
  submit_attempted: boolean;
  outcome_ambiguous: boolean;
  retry_safety_receipt?: RetrySafetyReceipt | null;
};
export type RetrySafetyReceipt = {
  schema_version: string;
  evidence_source: 'same_session_observation' | 'idempotency_lookup';
  observation_completed: boolean;
  observed_target_state: 'confirmed' | 'rejected_without_effect' | 'unchanged_pre_effect' | 'unknown';
  same_action_session: boolean;
  evidence_step_run_id: string;
  evidence_refs: string[];
};
export type AttemptRetryHistoryEntry = {
  attempt: number;
  signal: AttemptRetrySignal;
};
export type RetryActionHistoryEntry = {
  attempt: number;
  event: 'child_scope_scheduled' | 'child_scope_dispatch_requested' | 'child_scope_retry_blocked_safety';
  retry_count?: number | null;
  step_run_ids?: string[];
  safety_reason_code?:
    | 'post_submit_evidence_unverified'
    | 'effectful_external_action_in_scope'
    | 'unresolved_dispatch_action_in_scope'
    | 'sub_automation_child_run_in_scope'
    | 'sub_automation_in_flight_in_scope'
    | null;
};
export type RetryPresentationState =
  | 'queued'
  | 'running'
  | 'automatically_retried'
  | 'succeeded'
  | 'stopped'
  | 'retry_limit_reached'
  | 'needs_review'
  | 'automatic_retry_withheld';
export type RetrySubmissionEvidence = 'attempted' | 'not_attempted' | 'unknown';
export type RetryAttemptPresentation = {
  attempt_number: number;
  ordinal: number;
  state: RetryPresentationState;
  title: string;
  detail: string | null;
  submission_evidence: RetrySubmissionEvidence;
  scope: {
    parent_path: string;
    child_index: 1;
    scope_parent_step_run_id: string;
    attempt_number: number;
  };
};
export type RetryPresentation = {
  schema_version: 'v1';
  attempt_count: number;
  retry_count: number;
  max_retry_count: number;
  state: RetryPresentationState;
  headline: string;
  detail: string | null;
  current_attempt: RetryAttemptPresentation;
  previous_attempts: RetryAttemptPresentation[];
};
export type ChildScopeState = {
  key: string | null;
  status: ExecutionStatus;
  terminal_message?: string | null;
  attempt_count?: number;
  attempt_retry_signal?: AttemptRetrySignal | null;
  attempt_retry_history?: AttemptRetryHistoryEntry[];
  retry_count?: number;
  retry_guidance?: ScopedRetryGuidance | null;
  retry_history?: ScopedRetryHistoryEntry[];
  retry_action_history?: RetryActionHistoryEntry[];
};
export type StepExecutionTerminalReasonContext = Record<string, unknown> & {
  browser_failure_diagnostic?: {
    schema_version: 'browser_failure_diagnostic.v1';
    reason_code: string;
    stage_id?: string | null;
    stage_label?: string | null;
    stage_executor?: string | null;
    secure_input?: {
      phase: 'configuration_preflight' | 'secure_input_use';
      secure_key_names: string[];
      observed_hostname?: string | null;
      allowed_hostnames: string[];
      operation_index?: number | null;
      operation_kind?: string | null;
      recipient_scope?: 'page' | 'frame' | null;
    } | null;
  } | null;
  reason_relation?: 'external_upstream' | 'child_step' | 'child_scope' | null;
  reason_scope_step_id?: string | null;
  reason_scope_path?: string | null;
  reason_child_index?: number | null;
  reason_subject_step_id?: string | null;
  reason_subject_step_name?: string | null;
  reason_subject_full_path?: string | null;
  reason_failed_child_count?: number | null;
  reason_paused_child_count?: number | null;
  upstream_step_id?: string | null;
  upstream_step_name?: string | null;
  upstream_full_path?: string | null;
  protected_scope_outcome?: 'upstream_skipped' | null;
  protected_scope_started?: boolean | null;
  terminal_cause?: {
    terminal_reason_code?: string | null;
  } | null;
};
export type CodeExecutionProvenance = {
  configured_code_source_mode?: string;
  effective_code_source?: 'cached' | 'provided' | 'generated';
  used_code_cache?: boolean;
  executed_code_sha256?: string | null;
};
export type StepExecution = {
  id: string;
  step_id: string;
  step_type: StepType;
  created_at: string;
  completed_at: string | null;
  status: ExecutionStatus;
  completion_disposition?: 'normal' | 'recovered';
  terminal_reason_code?: string | null;
  terminal_reason_context?: StepExecutionTerminalReasonContext | null;
  child_scope_state?: ChildScopeState[] | null;
  code_execution_provenance?: CodeExecutionProvenance | null;
};

export const getStepExecutionChildScopeState = (stepExecution?: StepExecution | null): ChildScopeState[] | null =>
  stepExecution?.child_scope_state ?? null;
// export type StepExecutions = PaginatedResponse<StepExecution>;
export type StepRun = {
  id: string;
  created_at?: string;
  parent_path: string | null;
  child_index: number | null;
  attempt_number: number;
  retry_parent_attempt_number?: number | null;
  full_path: string;
  is_latest: boolean;
  step_execution?: StepExecution;
  step_execution_id: string;
  step_id: string;
  step_index: number;
  step_type: StepType;
  used_existing_step_execution: boolean;
  is_preserved_rerun_carrier: boolean;
  execution: Execution;
  rerun_eligibility?: {
    allowed: boolean;
    reason: string | null;
    expected_base_execution_id: string | null;
    source_step_run_id: string;
    parent_step_run_ids: string[];
  } | null;
};

// only used manually in the ui
export type ParentStepRun = StepRun & {
  step_execution: StepExecution;
};

export type StepsRuns = {
  step_runs_by_step_id: Record<string, StepRun[]>;
  retained_prior_scope_by_step_id?: Record<string, RetainedPriorScopeStepRun[]>;
};

export type RetainedPriorScopeStepRun = {
  kind: 'retained_prior_scope';
  source_child_scope_execution_id: string;
  source_step_run: StepRun;
};

export type RuntimeScopeQuery = {
  parentPath?: string | null;
  childIndex?: number | null;
  scopeParentStepRunId?: string | null;
};

export type StepRunScopeQuery = RuntimeScopeQuery & {
  attemptNumber?: number | null;
};

export type StepRunHistory = {
  run_id: string;
  step_id: string;
  full_path: string;
  total_attempt_count: number;
  attempts: StepRun[];
};

export type WaitingStepDependency = {
  step_id: string;
  full_path: string;
  status: ExecutionStatus | null;
  dependency_type: 'data' | 'control';
};

export type WaitingStepDependencies = {
  waiting_dependencies_by_step_id: Record<string, WaitingStepDependency[]>;
};

const UPSTREAM_BLOCKED_REASON_CODES = new Set([
  'upstream_failed',
  'upstream_cancelled',
  'upstream_completed_with_issues',
]);

export const isUpstreamBlockedReasonCode = (reasonCode?: string | null) =>
  !!reasonCode && UPSTREAM_BLOCKED_REASON_CODES.has(reasonCode);
